import type { core, util } from 'zod/mini';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    if (Object.prototype.toString.call(value) !== '[object Object]') {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

/**
 * Converts empty strings (`''`) to `undefined` recursively.
 *
 * This keeps validation behavior consistent with our historical form semantics and avoids
 * surprises like `z.coerce.number()` treating an empty string as `0`.
 */
const prepareDataForValidation = (values: unknown): unknown => {
    if (Array.isArray(values)) {
        return values.map((value) => {
            if (Array.isArray(value) || isPlainObject(value)) {
                return prepareDataForValidation(value);
            }
            return value !== '' ? value : undefined;
        });
    }

    if (isPlainObject(values)) {
        const data: Record<string, unknown> = {};
        for (const k in values) {
            if (!Object.prototype.hasOwnProperty.call(values, k)) {
                continue;
            }
            const key = String(k);
            const value = values[key];

            if (Array.isArray(value)) {
                data[key] = value.map((item) => {
                    if (Array.isArray(item) || isPlainObject(item)) {
                        return prepareDataForValidation(item);
                    }
                    return item !== '' ? item : undefined;
                });
                continue;
            }

            if (isPlainObject(value)) {
                data[key] = prepareDataForValidation(value);
                continue;
            }

            data[key] = value !== '' ? value : undefined;
        }
        return data;
    }

    return values !== '' ? values : undefined;
};

export type ZodSchemaLike<TOutput = unknown> = {
    parseAsync: (data: unknown, params?: core.ParseContext<core.$ZodIssue>) => Promise<TOutput>;
    safeParseAsync: (
        data: unknown,
        params?: core.ParseContext<core.$ZodIssue>,
    ) => Promise<util.SafeParseResult<TOutput>>;
};

type SchemaOutput<TSchema> = TSchema extends ZodSchemaLike<infer TOutput> ? TOutput : never;

/**
 * Convert a Zod schema into a form-level validate function (returns a nested object of string
 * errors keyed by field path).
 */
export const toFormValidate = <TSchema extends ZodSchemaLike>(
    schema: TSchema,
    params?: Partial<core.ParseContext<core.$ZodIssue>>,
) => {
    return async (values: SchemaOutput<TSchema>) => {
        const result = await schema.safeParseAsync(prepareDataForValidation(values), params);
        if (result.success) {
            return;
        }
        const validationErrors: Record<string, any> = {};

        const setNestedError = (path: PropertyKey[], message: string) => {
            const parts = path.filter((part) => part != null).map(String);
            if (parts.length === 0) {
                return;
            }
            let cursor: Record<string, any> = validationErrors;
            for (let i = 0; i < parts.length - 1; i++) {
                const key = parts[i];
                const next = cursor[key];
                if (typeof next !== 'object' || next == null) {
                    cursor[key] = {};
                }
                cursor = cursor[key];
            }
            cursor[parts[parts.length - 1]] = message;
        };

        for (const issue of result.error.issues) {
            setNestedError(issue.path, issue.message);
        }
        return validationErrors;
    };
};
